import * as React from "react";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import { useWebSocket, ReadyState } from "@/lib/cjs-interop";
import type { StreamEvent } from "../types";

const STREAM_TIMEOUT_MS = 5 * 60 * 1000;

interface UseColumnAnalysisStreamOptions {
  conversationId: string | null;
  onComplete?: (content: string) => void;
}

export const useColumnAnalysisStream = ({ conversationId, onComplete }: UseColumnAnalysisStreamOptions) => {
  const [content, setContent] = React.useState("");
  const [status, setStatus] = React.useState<"idle" | "running" | "completed" | "error">("idle");
  const contentRef = React.useRef("");
  const onCompleteRef = React.useRef(onComplete);
  onCompleteRef.current = onComplete;
  const lastMessageTimeRef = React.useRef<number>(0);

  const { lastJsonMessage, readyState, sendJsonMessage } = useWebSocket<StreamEvent>(
    conversationId ? "/pf-mainweb/business-reports/ws" : null,
    {
      shouldReconnect: event => event?.code !== 1000,
      skipAssert: true,
      reconnectInterval: 3000,
      onError: error => {
        Sentry.captureException(error);
      },
    },
  );

  React.useEffect(() => {
    setContent("");
    contentRef.current = "";
    setStatus("idle");
  }, [conversationId]);

  React.useEffect(() => {
    if (!lastJsonMessage || !conversationId || lastJsonMessage.conversation_id !== conversationId) return;

    lastMessageTimeRef.current = Date.now();

    if (lastJsonMessage.type === "start") {
      setContent("");
      contentRef.current = "";
      setStatus("running");
    } else if (lastJsonMessage.type === "msg") {
      contentRef.current += lastJsonMessage.t;
      setContent(contentRef.current);
    } else if (lastJsonMessage.type === "end") {
      setStatus("completed");
      onCompleteRef.current?.(contentRef.current);
    } else if (lastJsonMessage.type === "error") {
      setStatus("error");
      toast.error(lastJsonMessage.message ?? "Analysis failed. Please try again.");
    }
  }, [lastJsonMessage, conversationId]);

  React.useEffect(() => {
    if (status !== "running") return;

    const interval = setInterval(() => {
      if (lastMessageTimeRef.current > 0 && Date.now() - lastMessageTimeRef.current > STREAM_TIMEOUT_MS) {
        setStatus("error");
        toast.error("Analysis timed out. Please try again.");
      }
    }, 10_000);

    return () => clearInterval(interval);
  }, [status]);

  React.useEffect(() => {
    if (readyState === ReadyState.OPEN && conversationId) {
      sendJsonMessage({ query_id: conversationId });
    }
  }, [readyState, conversationId, sendJsonMessage]);

  return {
    content,
    status,
    isStreaming: status === "running",
  };
};
